// src/components/Dashboard/BusinessMetrics.js
import React, { useMemo } from 'react';
import { RefreshCw } from 'lucide-react';
import { formatCurrency, formatNumber } from '../../utils/helpers';
import { theme, rgba } from '../../theme';

// Pure dollar-value tiles (Monthly Revenue, Net Profit, and Dashboard.js's Avg Bill
// Rate/Avg Employee Pay/Gross Profit) route to the Financial Reporting page (Phase 1 -
// see MEMORY: financial_reporting_dashboard_scoping) rather than AIM's Finance domain,
// which is a deliberately alerts-first view (aimFinanceAssembler.js) - a different
// intent than clicking a healthy metric on this dashboard. Business Health keeps its
// AIM Finance link - its own framing (Excellent/Good/Needs Attention, unstaffed-
// contract count) is genuinely "is something wrong," which matches what Finance domain
// actually answers. Clients (was "Client Sites" - see MEMORY:
// clients_page_and_sites_fallback_fix) now routes to the real Clients page instead of
// AIM Scheduling, now that page exists. Active Employees goes to Team Management,
// plain navigation.
const BusinessMetrics = ({ data, onRefresh, onNavigate }) => {
  const metrics = useMemo(() => {
    const baseMetrics = [
      {
        label: 'Monthly Revenue',
        value: data?.analytics?.monthlyRevenue ? formatCurrency(data.analytics.monthlyRevenue) : '$0',
        change: data?.analytics?.profitBreakdown?.profitMargin > 0 ?
          `${data.analytics.profitBreakdown.profitMargin.toFixed(1)}% margin` : '+0%',
        icon: '💰',
        color: '#22c55e',
        gradient: 'linear-gradient(135deg, rgba(34, 197, 94, 0.2), rgba(34, 197, 94, 0.1))',
        details: data?.analytics?.profitBreakdown ?
          `Gross Profit: ${formatCurrency(data.analytics.profitBreakdown.grossProfit)}` : null,
        nav: { key: 'financial-reports' }
      },
      {
        label: 'Active Employees',
        // Was data?.employees?.length - the RAW roster, active and inactive together
        // (showed 9 instead of the real 7 active). data.analytics.activeEmployees
        // (useBusinessData.js) already filters to status === 'Active' - the tile's own
        // "change" subtext below was already built from that same active-filtered data
        // (roleBreakdowns), just not the headline number.
        value: formatNumber(data?.analytics?.activeEmployees ?? 0),
        change: data?.analytics?.roleBreakdowns ?
          `${data.analytics.roleBreakdowns.drivers} drivers, ${data.analytics.roleBreakdowns.staticGuards} static` :
          '+0%',
        icon: '👮',
        color: '#3b82f6',
        gradient: 'linear-gradient(135deg, rgba(59, 130, 246, 0.2), rgba(59, 130, 246, 0.1))',
        details: data?.analytics?.roleBreakdowns ?
          `${data.analytics.roleBreakdowns.supervisors} supervisors` : null,
        // Plain navigation, no filter param - TeamPage's only filter is user-account
        // status (active/pending/suspended), not employment status (Active/Inactive/On
        // Leave) - the two are genuinely different concepts, so this deliberately does
        // NOT claim to pre-filter to something that isn't real. See App.js's note.
        nav: { key: 'team' }
      },
      {
        // Was labeled "Client Sites" reading data?.analytics?.totalSites, which
        // silently fell back to the client count whenever sites.length was 0 (i.e.
        // always - company_data.json's sites array is empty and no client has a real
        // location/address field at all). That's the same client count as
        // totalClients, just under a misleading label implying physical locations
        // that don't exist as data. Reads totalClients directly now, and the label
        // matches what it's actually always shown.
        label: 'Clients',
        value: formatNumber(data?.analytics?.totalClients || 0),
        change: data?.analytics?.totalClients > 0 ?
          `${data.analytics.totalClients} clients` : '+0%',
        icon: '🏢',
        color: '#f59e0b',
        gradient: 'linear-gradient(135deg, rgba(245, 158, 11, 0.2), rgba(245, 158, 11, 0.1))',
        details: data?.analytics?.clientDetails?.length > 0 ?
          `Top: ${data.analytics.clientDetails[0]?.name}` : null,
        nav: { key: 'clients' }
      },
      {
        label: 'Business Health',
        value: data?.analytics?.businessHealth || 'Unknown',
        change: data?.analytics?.profitMargin > 20 ? 'Excellent' :
               data?.analytics?.profitMargin > 10 ? 'Good' : 'Needs Attention',
        icon: '📊',
        color: data?.analytics?.profitMargin > 20 ? '#22c55e' :
               data?.analytics?.profitMargin > 10 ? '#f59e0b' : '#ef4444',
        gradient: 'linear-gradient(135deg, rgba(139, 92, 246, 0.2), rgba(139, 92, 246, 0.1))',
        details: (() => {
          if (!data?.analytics?.profitBreakdown) return null;
          const laborPct = `Labor: ${((data.analytics.profitBreakdown.totalLaborCosts / data.analytics.profitBreakdown.totalRevenue) * 100).toFixed(0)}%`;
          const gaps = data.analytics.clientsNeedingStaffing || [];
          return gaps.length > 0 ? `${laborPct} — ⚠ ${gaps.length} contract${gaps.length > 1 ? 's' : ''} unassigned` : laborPct;
        })(),
        nav: { key: 'aim', params: { initialDomain: 'finance' } }
      },
      {
        label: 'Net Profit',
        value: data?.analytics?.netProfit !== null && data?.analytics?.netProfit !== undefined
          ? formatCurrency(data.analytics.netProfit) : '—',
        change: data?.analytics?.netProfitMargin !== null && data?.analytics?.netProfitMargin !== undefined
          ? `${data.analytics.netProfitMargin.toFixed(1)}% margin` : '+0%',
        icon: '🧾',
        color: '#a855f7',
        gradient: 'linear-gradient(135deg, rgba(168, 85, 247, 0.2), rgba(168, 85, 247, 0.1))',
        details: (() => {
          const method = data?.analytics?.overheadMethod;
          const amount = data?.analytics?.overheadAmount;
          if (method === 'flat_expenses') return `Overhead: ${formatCurrency(amount)}/mo (stated directly)`;
          if (method === 'oer_derived') return `Overhead: ${formatCurrency(amount)} (derived from OER)`;
          if (method === 'ambiguous_needs_choice') return `Both OER and overhead expenses on file — choose a method in chat`;
          return `Excludes overhead — not yet configured`;
        })(),
        nav: { key: 'financial-reports' }
      }
    ];

    return baseMetrics;
  }, [data]);

  return (
    <div style={{ marginBottom: '32px' }}>
      <div style={{ 
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '16px'
      }}>
        <h2 style={{ 
          color: '#e2e8f0', 
          fontSize: '24px', 
          margin: 0,
          fontWeight: '600' 
        }}>
          📈 Business Metrics
        </h2>
        {onRefresh && (
          <button
            onClick={onRefresh}
            style={{
              background: rgba(theme.colors.primaryRgb, 0.2),
              border: `1px solid ${rgba(theme.colors.primaryRgb, 0.3)}`,
              borderRadius: '8px',
              color: theme.colors.primary,
              padding: '8px 16px',
              fontSize: '14px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontWeight: '500'
            }}
            title="Refresh metrics"
          >
            <RefreshCw size={16} />
            Refresh
          </button>
        )}
      </div>

      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', 
        gap: '24px'
      }}>
        {metrics.map((metric, index) => (
          <div key={index}
          onClick={() => metric.nav && onNavigate?.(metric.nav.key, metric.nav.params)}
          style={{
            padding: '28px',
            background: rgba(theme.colors.tileBackgroundRgb, 0.8),
            border: `1px solid ${rgba(theme.colors.tileBorderRgb, 0.3)}`,
            borderRadius: '16px',
            backdropFilter: 'blur(12px)',
            position: 'relative',
            overflow: 'hidden',
            cursor: (metric.nav && onNavigate) ? 'pointer' : 'default',
            transition: 'all 0.3s ease'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-4px)';
            e.currentTarget.style.boxShadow = `0 12px 35px ${metric.color}40`;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = 'none';
          }}
          >
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: metric.gradient,
              opacity: 0.5
            }} />
            
            <div style={{ position: 'relative', zIndex: 1 }}>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between', 
                marginBottom: '12px' 
              }}>
                <div style={{ fontSize: '32px' }}>{metric.icon}</div>
                <div style={{ 
                  padding: '4px 8px',
                  background: metric.change.includes('%') && !metric.change.includes('0%')
                    ? 'rgba(34, 197, 94, 0.2)'
                    : 'rgba(107, 114, 128, 0.2)',
                  borderRadius: '12px',
                  color: metric.change.includes('%') && !metric.change.includes('0%')
                    ? '#22c55e'
                    : '#6b7280',
                  fontSize: '12px',
                  fontWeight: '600'
                }}>
                  {metric.change}
                </div>
              </div>
              <div style={{ 
                fontSize: '36px', 
                fontWeight: 'bold', 
                color: metric.color, 
                marginBottom: '8px' 
              }}>
                {metric.value}
              </div>
              <div style={{ color: '#94a3b8', fontSize: '16px', fontWeight: '500' }}>
                {metric.label}
              </div>
              {metric.details && (
                <div style={{ color: '#6b7280', fontSize: '12px', marginTop: '4px' }}>
                  {metric.details}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default BusinessMetrics; 
